import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useCallback } from "react";

import { $workspaceAccountStatus } from "../../internal/workspaces/workspace-checkout.atom";
import { type Workspace } from "../../internal/workspaces/workspace.types";

/**
 * Hook for observing and refreshing a workspace's billing account status.
 *
 * Returns the raw status atom value (string, error, or null), loading/error flags,
 * and a memoized `refresh` helper that re-runs the underlying atomWithRefresh.
 */
export const useWorkspaceAccountStatus = (workspace: Workspace | undefined) => {
	const statusAtom = $workspaceAccountStatus(workspace);
	const statusLoadable = useAtomValue(loadable(statusAtom));
	const status = statusLoadable.state === "hasData" ? statusLoadable.data : null;
	const refreshStatus = useSetAtom(statusAtom);
	const refresh = useCallback(() => {
		if (!workspace) {
			return;
		}

		return refreshStatus();
	}, [refreshStatus, workspace]);

	return {
		status,
		refresh,
		isLoading: !workspace || statusLoadable.state === "loading",
		isError: !!workspace && statusLoadable.state === "hasError",
		error:
			!!workspace && statusLoadable.state === "hasError" ? statusLoadable.error : undefined,
	};
};
